/**
 * AI Recruit - Resume Formatter Template API Service
 * API calls for per-client AI-generated resume formatting templates.
 */

import { apiV2 } from './api'

export interface ResumeFormatTemplate {
  id: string
  client: string
  source_filename: string
  is_active: boolean
  is_draft: boolean
  generation_engine: string
  created_at: string | null
  updated_at: string | null
}

export interface BlueprintLocation {
  location_id: string
  kind: 'paragraph' | 'table_cell' | 'repeat_group'
  text_preview: string
  is_heading: boolean
  within_repeat_group: string | null
  bound_concept: string | null
  literal_text: string | null
  is_unbound: boolean
  instance_count: number | null
  block_index: number | null
  row: number | null
  col: number | null
}

export interface BlueprintMapResponse {
  id: string
  is_draft: boolean
  generation_engine: string
  available_concepts: string[]
  // The subset of available_concepts that are list-shaped (education,
  // certifications, professional_experience, ...) rather than scalar -
  // see BlueprintMapEditor.tsx's save-grouping logic for why this matters.
  array_concepts?: string[]
  example_analysis_json: Record<string, unknown>
  locations: BlueprintLocation[]
}

export interface BlueprintBinding {
  concept: string
  target: Record<string, unknown>
}

export interface BlueprintLiteralOverride {
  anchor: Record<string, unknown>
  text: string
}

export interface BlueprintUnboundAnchor {
  anchor: Record<string, unknown>
}

export interface ResumeFormatTemplateListResponse {
  data: ResumeFormatTemplate[]
  page: number
  page_size: number
  total_count: number
}

export interface DocumentMapResponse {
  id: string
  is_draft: boolean
  template_html: string
  example_analysis_json: Record<string, unknown>
}

export const fetchResumeFormatTemplates = async (params: {
  client?: string
  is_active?: boolean
  page?: number
  page_size?: number
}): Promise<ResumeFormatTemplateListResponse> => {
  const response = await apiV2.get<ResumeFormatTemplateListResponse>('/resume-templates', { params })
  return response.data
}

export const prepareResumeFormatTemplate = async (data: {
  client: string
  example_resume: File
}): Promise<ResumeFormatTemplate> => {
  const formData = new FormData()
  formData.append('client', data.client)
  formData.append('example_resume', data.example_resume)
  const response = await apiV2.post<ResumeFormatTemplate>('/resume-templates/prepare', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // The backend retries generation up to 5 times on a leak/completeness
    // failure (each attempt making 2+ LLM calls), which can legitimately
    // take longer than the app's 2-minute default timeout even when it's
    // working correctly and would eventually succeed - the browser aborts
    // client-side first, surfacing a generic "Failed to generate template"
    // while the backend is still actively generating.
    timeout: 300000, // 5 minutes
  })
  return response.data
}

export const updateResumeFormatTemplateStatus = async (
  templateId: string,
  isActive: boolean
): Promise<ResumeFormatTemplate> => {
  const response = await apiV2.patch<ResumeFormatTemplate>(`/resume-templates/${templateId}/status`, {
    is_active: isActive,
  })
  return response.data
}

export const deleteResumeFormatTemplate = async (templateId: string): Promise<void> => {
  await apiV2.delete(`/resume-templates/${templateId}`)
}

export const fetchDocumentMap = async (templateId: string): Promise<DocumentMapResponse> => {
  const response = await apiV2.get<DocumentMapResponse>(`/resume-templates/${templateId}/document-map`)
  return response.data
}

export const saveDocumentMap = async (
  templateId: string,
  templateHtml: string,
  activate: boolean
): Promise<ResumeFormatTemplate> => {
  const response = await apiV2.put<ResumeFormatTemplate>(`/resume-templates/${templateId}/document-map`, {
    template_html: templateHtml,
    activate,
  })
  return response.data
}

export const previewDocumentMap = async (templateId: string, templateHtml: string): Promise<Blob> => {
  const response = await apiV2.post(
    `/resume-templates/${templateId}/document-map/preview`,
    { template_html: templateHtml },
    { responseType: 'blob' }
  )
  return response.data
}

export const testDocumentMapWithResume = async (
  templateId: string,
  templateHtml: string,
  resumeAnalysis: Record<string, unknown>
): Promise<Blob> => {
  const response = await apiV2.post(
    `/resume-templates/${templateId}/document-map/test-with-resume`,
    { template_html: templateHtml, resume_analysis: resumeAnalysis },
    { responseType: 'blob' }
  )
  return response.data
}

export const fetchBlueprintMap = async (templateId: string): Promise<BlueprintMapResponse> => {
  const response = await apiV2.get<BlueprintMapResponse>(`/resume-templates/${templateId}/blueprint-map`)
  return response.data
}

export const saveBlueprintMap = async (
  templateId: string,
  data: {
    bindings: BlueprintBinding[]
    literal_overrides: BlueprintLiteralOverride[]
    unbound_anchors: BlueprintUnboundAnchor[]
    activate: boolean
  }
): Promise<ResumeFormatTemplate> => {
  const response = await apiV2.put<ResumeFormatTemplate>(`/resume-templates/${templateId}/blueprint-map`, data)
  return response.data
}

export const previewResumeFormatTemplate = async (templateId: string): Promise<Blob> => {
  const response = await apiV2.post(`/resume-templates/${templateId}/preview`, null, { responseType: 'blob' })
  return response.data
}

export const testResumeFormatTemplateWithResume = async (
  templateId: string,
  resumeAnalysis: Record<string, unknown>
): Promise<Blob> => {
  const response = await apiV2.post(
    `/resume-templates/${templateId}/test-with-resume`,
    { resume_analysis: resumeAnalysis },
    { responseType: 'blob' }
  )
  return response.data
}

export const lookupActiveResumeFormatTemplate = async (
  client: string
): Promise<{ exists: boolean; id?: string }> => {
  const response = await apiV2.get('/resume-templates/lookup', { params: { client } })
  return response.data
}

// This call's SUCCESS response is a binary .docx, so it must request
// responseType: 'blob' - but axios then applies that SAME responseType to
// an ERROR response from this endpoint too, so a structured JSON error
// body (e.g. { code: "TEMPLATE_INVALID", ... }) arrives as an opaque Blob
// instead of parsed JSON, unlike every other endpoint in this app (whose
// error bodies axios already parses automatically since they don't set
// responseType). Normalized here, at the one call site that actually
// needs it, rather than in the shared apiV2 interceptor (which every
// OTHER, non-blob endpoint also goes through and doesn't have this
// problem) - callers can keep reading error.response.data.code/.message
// exactly like any other endpoint's error, with no special-casing.
const normalizeBlobErrorResponse = async (error: any): Promise<void> => {
  const data = error?.response?.data
  if (data instanceof Blob && data.type.includes('application/json')) {
    try {
      error.response.data = JSON.parse(await data.text())
    } catch {
      // Not valid JSON after all - leave the raw Blob in place rather than
      // guessing at its shape.
    }
  }
}

export const formatResumeWithTemplate = async (
  resumeId: string,
  client: string,
  withLogo: boolean = false
): Promise<Blob> => {
  try {
    const response = await apiV2.get(`/resume-templates/format/${resumeId}`, {
      params: { client, with_logo: withLogo },
      responseType: 'blob',
    })
    return response.data
  } catch (error) {
    await normalizeBlobErrorResponse(error)
    throw error
  }
}
